import { create } from 'zustand';
import { ParticipantRole } from '@/consts/meeting.ts';
import { combine } from 'zustand/middleware';
import Api from '@/api/api.ts';
import { AccountRole } from '@/types/api.ts';

interface UserState {
  userUid: string;
  userName: string;
  isAuthenticated: boolean;
  // becomes true once the initial /auth/users/me probe has resolved (success OR
  // failure) — used by ProtectedRoute to avoid a flash-redirect before we know.
  hydrated: boolean;
  // authenticated account identity (separate from the per-meeting role below)
  email: string;
  accountRole: AccountRole | undefined;
  authUserId: string;
  authName: string;
  avatarUrl: string | null;
  role: ParticipantRole | undefined;
  participantActiveCanvas: 'canvasFile' | 'canvasBoard';
}

interface UserActions {
  setUserUid: (userUid: string) => void;
  setUserName: (userName: string) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setRole: (role: ParticipantRole | undefined) => void;
  setParticipantActiveCanvas: (canvas: 'canvasFile' | 'canvasBoard') => void;
  // probe the auth cookie and populate account state; safe to call on every load
  hydrate: () => Promise<void>;
  logout: () => Promise<void>;
}

export const useUserStore = create(
  combine<UserState, UserActions>(
    {
      userUid: '',
      userName: '',
      isAuthenticated: false,
      hydrated: false,
      email: '',
      accountRole: undefined,
      authUserId: '',
      authName: '',
      avatarUrl: null,
      role: undefined,
      participantActiveCanvas: 'canvasFile'
    },
    (set) => ({
      setUserUid: (userUid) => set({ userUid }),
      setUserName: (userName) => set({ userName }),
      setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
      setRole: (role) => set({ role }),
      setParticipantActiveCanvas: (participantActiveCanvas) => set({ participantActiveCanvas }),
      hydrate: async () => {
        try {
          const user = await Api.getCurrentUser();
          set({
            isAuthenticated: true,
            email: user.email,
            accountRole: user.role,
            authUserId: user.id,
            authName: user.name || user.email,
            avatarUrl: user.avatar_url,
            hydrated: true
          });
        } catch {
          set({
            isAuthenticated: false,
            email: '',
            accountRole: undefined,
            authUserId: '',
            authName: '',
            avatarUrl: null,
            hydrated: true
          });
        }
      },
      logout: async () => {
        try {
          await Api.logout();
        } catch {
          // ignore network errors on logout — clear local state regardless
        }
        set({
          isAuthenticated: false,
          email: '',
          accountRole: undefined,
          authUserId: '',
          authName: '',
          avatarUrl: null
        });
      }
    })
  )
);
